import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, ArrowLeft, Clock, CheckCircle2, AlertTriangle, ChevronRight, RefreshCw
} from 'lucide-react';
import QuestionBankClient from '../data/api/QuestionBankClient';
import CustomSelect, { SelectOption } from '../components/CustomSelect';
import Loader from '../../../../app/shared/components/Loader';
import { formatToLocalDateString } from '../../../../app/shared/utils/dateUtils';
import '../styles/QuestionBankStyles.css';

interface RequestItem {
  request_id: string;
  module_id: string;
  module_name?: string;
  request_datetime?: string;
  request_status: string;
  no_of_questions_requested?: number;
  no_of_questions_generated?: number;
  no_of_question_approved?: number;
  no_of_question_pending?: number;
  no_of_question_rejected?: number;
}

const AIGenerationRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<RequestItem[]>([]);
  const [modules, setModules] = useState<{ module_id: string; module_name: string }[]>([]);
  const [selectedModule, setSelectedModule] = useState<string>('All');
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);

  useEffect(() => {
    fetchModules();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [selectedModule]);

  useEffect(() => {
    fetchRequests();
  }, [selectedModule, page]);

  const fetchModules = async () => {
    try {
      const res = await QuestionBankClient.listModules();
      if (res && res.modules) {
        setModules(res.modules);
      }
    } catch (err) {
      console.error('Error fetching modules:', err);
    }
  };

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const res = await QuestionBankClient.listAIGenerationRequests(
        selectedModule !== 'All' ? selectedModule : undefined,
        page,
        limit
      );
      if (res && res.requests) {
        setRequests(res.requests);
        setTotalCount(res.total_count || res.total || 0);
      } else {
        setRequests([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error fetching AI requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const moduleOptions: SelectOption[] = [
    { value: 'All', label: 'All Modules' },
    ...modules.map((m) => ({ value: m.module_id, label: m.module_name }))
  ];

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Success':
      case 'Completed':
        return (
          <span className="qb-badge qb-badge-easy" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <CheckCircle2 size={12} /> Success
          </span>
        );
      case 'InProgress':
        return (
          <span className="qb-badge qb-badge-medium" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <Clock size={12} /> Processing
          </span>
        );
      case 'Failed':
        return (
          <span className="qb-badge qb-badge-hard" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <AlertTriangle size={12} /> Failed
          </span>
        );
      default:
        return <span className="qb-badge">{status}</span>;
    }
  };

  return (
    <div className="qb-container">
      <Loader isLoading={loading} message="Loading AI Generation Requests..." />

      {/* Header matching Corporate standard */}
      <div className="qb-header">
        <div>
          <h1 className="qb-title">
            <Sparkles size={24} color="#0d6e5b" /> AI Question Generation History
          </h1>
          <p className="qb-subtitle">
            Track AI generated question batches, review status, and approve questions for your modules.
          </p>
        </div>

        <div className="qb-header-actions">
          <button className="qb-btn qb-btn-outline" onClick={fetchRequests}>
            <RefreshCw size={16} /> Refresh
          </button>
          <button className="qb-btn qb-btn-secondary" onClick={() => navigate('/dashboard/question-bank/ai-generate')}>
            <ArrowLeft size={16} /> Back to Generate AI Question
          </button>
        </div>
      </div>

      {/* Filter Card */}
      <div className="qb-filter-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <span style={{ fontSize: '14px', fontWeight: 600, color: '#475569' }}>Filter by Module:</span>
          <CustomSelect
            options={moduleOptions}
            value={selectedModule}
            onChange={(val) => setSelectedModule(val)}
            placeholder="Filter by Module..."
            style={{ width: '260px' }}
          />
        </div>
      </div>

      {/* History Table */}
      <div className="qb-table-card">
        {requests.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#64748b' }}>
            <Sparkles size={48} style={{ opacity: 0.3, marginBottom: '12px' }} />
            <h3>No AI Generation History Found</h3>
            <p>Generated question batches will appear here along with status tracking and approval progress.</p>
          </div>
        ) : (
          <div className="qb-table-scroll">
            <table className="qb-table">
              <thead>
                <tr>
                  <th style={{ width: '60px', textAlign: 'center' }}>Q.no</th>
                  <th>Date & Time</th>
                  <th>Module Name</th>
                  <th>Status</th>
                  <th>Requested</th>
                  <th>Generated</th>
                  <th>Approved</th>
                  <th>Rejected</th>
                  <th>Pending Review</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req, idx) => (
                  <tr
                    key={req.request_id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate('/dashboard/question-bank/ai-review', { state: { requestId: req.request_id, moduleId: req.module_id } })}
                  >
                    <td style={{ textAlign: 'center', fontWeight: 600, color: '#64748b', fontSize: '13px' }}>
                      {(page - 1) * limit + idx + 1}
                    </td>
                    <td style={{ fontWeight: 500 }}>{formatToLocalDateString(req.request_datetime)}</td>
                    <td>
                      <span style={{ fontWeight: 600, color: '#0d6e5b' }}>{req.module_name || 'General'}</span>
                    </td>
                    <td>{getStatusBadge(req.request_status)}</td>
                    <td><strong>{req.no_of_questions_requested || 0}</strong></td>
                    <td><strong>{req.no_of_questions_generated || 0}</strong></td>
                    <td style={{ color: '#15803d', fontWeight: 600 }}>{req.no_of_question_approved || 0}</td>
                    <td style={{ color: '#dc2626', fontWeight: 600 }}>{req.no_of_question_rejected || 0}</td>
                    <td>
                      <span className="qb-badge qb-badge-medium">{req.no_of_question_pending || 0} Pending</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="qb-btn qb-btn-outline"
                        style={{ padding: '6px 12px', fontSize: '12.5px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/dashboard/question-bank/ai-review', { state: { requestId: req.request_id, moduleId: req.module_id } });
                        }}
                      >
                        <span>Review Questions</span>
                        <ChevronRight size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="qb-pagination">
            <div>
              Showing {Math.min((page - 1) * limit + 1, totalCount)} to{' '}
              {Math.min(page * limit, totalCount)} of {totalCount} requests
            </div>
            <div className="qb-pagination-controls">
              <button
                className="qb-btn qb-btn-outline"
                disabled={page <= 1}
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              >
                Previous
              </button>
              <button
                className="qb-btn qb-btn-outline"
                disabled={page * limit >= totalCount}
                onClick={() => setPage((prev) => prev + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIGenerationRequestsPage;
